import { useMemo, type CSSProperties } from 'react'
import { Link, useLocation } from 'react-router'
import { Sidebar } from '@carouselgg/nexus/sidebar'
import type { SidebarEntry } from '@carouselgg/nexus/sidebar'
import { BookOpen } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import { MainNavHeader } from './mainNav'
import { useMainNav } from './useMainNav'
import { MobileLogoStrip } from './MobileLogoStrip'
import { HubSidebarHeader } from './hub/HubSidebarHeader'
import { hubSections } from './hub/hubSections'
import { isHubPath } from './hub/hubReturn'
import { useHubReturn } from './hub/useHubReturn'
import styles from './AppSidebar.module.css'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
  style?: CSSProperties
  collapsed?: boolean
  /** Render as an on-demand overlay drawer at all widths (used on the landing page). */
  floating?: boolean
  className?: string
}

/**
 * The app shell's side nav: the main nav (see mainNav.tsx, shared with an
 * org's management area), with your teams and orgs in the logo rail beside it.
 *
 * Inside the account hub (/settings) the nav changes: the places give way to
 * the hub's own sections, under a link back to the page you came from.
 */
export function AppSidebar({ isOpen, onClose, style, collapsed, floating, className }: AppSidebarProps) {
  const nav = useMainNav({ collapsed })
  const { pathname } = useLocation()
  const back = useHubReturn()
  const inHub = nav.isAuthenticated && isHubPath(pathname)
  const user = useAuthStore((s) => s.user)
  const phoneVerified = useAuthStore((s) => s.phoneVerified)
  const hasPasskey = useAuthStore((s) => s.hasPasskey)
  const signInMissing = (user && !user.email_verified ? 1 : 0) + (phoneVerified ? 0 : 1) + (hasPasskey ? 0 : 1)
  const memberships = nav.teams.length + nav.orgs.length

  const items = useMemo<SidebarEntry[]>(
    () => (inHub ? hubSections({ memberships, signInMissing }) : nav.sections),
    [inHub, memberships, signInMissing, nav.sections],
  )

  return (
    <Sidebar
      items={items}
      isOpen={isOpen}
      onClose={onClose}
      style={style}
      className={className}
      collapsed={collapsed}
      floating={floating}
      storageKeyPrefix="app"
      onItemClick={onClose}
      header={
        inHub
          ? (
            <>
              <HubSidebarHeader back={back} collapsed={collapsed} onNavigate={onClose} />
              <MobileLogoStrip groups={nav.groups} onNavigate={onClose} className={styles.mobileOnly} />
            </>
          )
          : <MainNavHeader nav={nav} collapsed={collapsed} onNavigate={onClose} />
      }
      footer={
        <Link
          to="/how-it-works"
          className={[styles.howItWorks, collapsed && styles.collapsed].filter(Boolean).join(' ')}
          onClick={onClose}
          aria-label={collapsed ? 'How it works' : undefined}
        >
          <BookOpen size={14} aria-hidden="true" />
          <span className={styles.howItWorksLabel}>How it works</span>
        </Link>
      }
    />
  )
}
